import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface MetaAuthState {
  isAuthenticated: boolean
  publicKey: string | null
  isLoading: boolean
  error: string | null
}

export const useMetaAuthStore = defineStore('metaAuth', () => {
  // State
  const isAuthenticated = ref(false)
  const publicKey = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Constants - expected hash and password for authentication
  const EXPECTED_PRIVATE_KEY_HASH = 'fa3ddd576349129e9f462d5077a6afd3969c7d3b36a8902dfb9cc88a8c133483d4a8d734cf445d651669f7c3a49c08c5eb53936ef4710a4de7e4493ccb44c6ab'
  const EXPECTED_PASSWORD = 'REALSAPP2026!'

  // Computed
  const isMetaAuthenticated = computed(() => isAuthenticated.value)

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const authenticateWithPrivateKey = async (privateKeyContent: string): Promise<boolean> => {
    // This function is kept for backward compatibility but now expects hash + password
    throw new Error('Please use authenticateWithCredentials instead')
  }

  const authenticateWithCredentials = async (fileHash: string, password: string): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)

      console.log('🔐 Authenticating with hash + password...')

      // Validate hash
      if (fileHash !== EXPECTED_PRIVATE_KEY_HASH) {
        console.error('❌ Hash mismatch')
        throw new Error('Invalid file hash - file does not match expected private key')
      }

      // Validate password
      if (password !== EXPECTED_PASSWORD) {
        console.error('❌ Password mismatch')
        throw new Error('Invalid password')
      }

      console.log('✅ Authentication successful - hash and password verified')
      isAuthenticated.value = true
      sessionStorage.setItem('metaAuth', 'true')

      return true

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
      setError(errorMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const authenticateWithKeyFile = async (file: File, password: string): Promise<boolean> => {
    try {
      console.log('🔐 Authenticating with key file:', file.name, '(', file.size, 'bytes)')
      setLoading(true)
      setError(null)

      // Validate file type
      if (!file.name.endsWith('.pem') && !file.name.endsWith('.key')) {
        throw new Error('Please select a .pem or .key file')
      }

      console.log('📖 Reading file content...')
      const content = await readFileAsText(file)
      console.log('✅ File content read successfully (', content.length, 'characters)')

      // Compute SHA-512 hash of the file content
      console.log('🔢 Computing file hash...')
      const fileHash = await computeSHA512(content)
      console.log('✅ File hash computed:', fileHash.substring(0, 16) + '...')

      // Authenticate with hash and password
      return await authenticateWithCredentials(fileHash, password)

    } catch (err) {
      console.error('❌ File authentication failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to read key file'
      setError(errorMessage)
      return false
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    isAuthenticated.value = false
    publicKey.value = null
    error.value = null
    sessionStorage.removeItem('metaAuth')
  }

  const checkAuthStatus = () => {
    // Check if user was previously authenticated in this session
    const wasAuthenticated = sessionStorage.getItem('metaAuth') === 'true'
    if (wasAuthenticated) {
      isAuthenticated.value = true
    }
    return isAuthenticated.value
  }

  // Helper functions
  const isValidPrivateKey = (content: string): boolean => {
    // Basic validation - check for key file markers
    return content.includes('-----BEGIN') &&
           (content.includes('RSA PRIVATE KEY') ||
            content.includes('OPENSSH PRIVATE KEY') ||
            content.includes('PRIVATE KEY'))
  }

  // Compute SHA-512 hash of content using Web Crypto API
  const computeSHA512 = async (content: string): Promise<string> => {
    const encoder = new TextEncoder()
    const data = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-512', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target?.result as string)
      reader.onerror = (e) => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  // Initialize on store creation
  checkAuthStatus()

  return {
    // State
    isAuthenticated,
    publicKey,
    isLoading,
    error,

    // Computed
    isMetaAuthenticated,

    // Actions
    authenticateWithPrivateKey,
    authenticateWithKeyFile,
    authenticateWithCredentials,
    logout,
    checkAuthStatus,
    setError
  }
})
